import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { useTranslation } from '../../utils/translations';
import { colors } from '../theme/theme';

import type { ContractApiResponse } from '../../services/contract-services';

interface CardIneligibleContractsPanelProps {
    cardIneligibleContracts: ContractApiResponse[];
    /** True when the payment table above is rendering rows — controls this panel's top border radius. */
    hasPaymentRows: boolean;
    /** All active contracts to check for AutoPay status */
    activeContracts?: ContractApiResponse[];
}

/** Warning panel listing contracts that are not eligible for card payment (shown below the payment table). */
export const CardIneligibleContractsPanel: React.FC<CardIneligibleContractsPanelProps> = ({
    cardIneligibleContracts,
    hasPaymentRows,
    activeContracts = [],
}) => {
    const { t } = useTranslation();

    // Separate ineligible contracts by AutoPay status
    const autoPayIneligibleContracts = cardIneligibleContracts.filter(ineligible => {
        const activeContract = activeContracts.find(
            active =>
                (active.AspireId || `Contract ${active.Id}`) === (ineligible.AspireId || `Contract ${ineligible.Id}`),
        );
        return activeContract?.AutoPay === true;
    });

    const otherIneligibleContracts = cardIneligibleContracts.filter(ineligible => {
        const activeContract = activeContracts.find(
            active =>
                (active.AspireId || `Contract ${active.Id}`) === (ineligible.AspireId || `Contract ${ineligible.Id}`),
        );
        return activeContract?.AutoPay !== true;
    });

    return (
        <Box
            sx={{
                mt: 4,
                mb: 3,
                borderRadius: hasPaymentRows ? '0 0 10px 10px' : 2,
                borderTop: `2px solid ${colors.errorPreset[40]}`,
                overflow: 'hidden',
                bgcolor: colors.greyPreset[5],
            }}
        >
            {/* AutoPay Section */}
            {autoPayIneligibleContracts.length > 0 && (
                <>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1.5 }}>
                        <span className="icon-info-filled" style={{ fontSize: 16, color: colors.errorPreset[40] }} />
                        <Typography variant="body2" fontWeight={700} sx={{ color: colors.errorPreset[40] }}>
                            {t('makePayment.cardPaymentDisabledAutoPay')}
                        </Typography>
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 2, pb: 2 }}>
                        {autoPayIneligibleContracts.map(contractItem => {
                            const contractId = contractItem.AspireId || `Contract ${contractItem.Id}`;
                            return (
                                <Box
                                    key={contractId}
                                    sx={{
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1.5,
                                        flex: '1 1 100px',
                                        maxWidth: 180,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary' }}>
                                        {contractId}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}

            {/* Other Ineligible Contracts Section */}
            {otherIneligibleContracts.length > 0 && (
                <>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1.5 }}>
                        <span className="icon-info-filled" style={{ fontSize: 16, color: colors.errorPreset[40] }} />
                        <Typography variant="body2" fontWeight={700} sx={{ color: colors.errorPreset[40] }}>
                            {t('makePayment.cardPaymentNotAvailableForLocation')}
                        </Typography>
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 2, pb: 2 }}>
                        {otherIneligibleContracts.map(contractItem => {
                            const contractId = contractItem.AspireId || `Contract ${contractItem.Id}`;
                            return (
                                <Box
                                    key={contractId}
                                    sx={{
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        px: 2,
                                        py: 1.5,
                                        flex: '1 1 100px',
                                        maxWidth: 180,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary' }}>
                                        {contractId}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
};
